import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type {
  ApiClient, FlbAbgleich, FlbAbschnitt, FlbBaum, FlbGewerk, FlbKapitel, FlbWert,
} from '../api';
import {
  IconEbene, IconOk, IconPdf, IconTabelle, IconWarnung,
} from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import { EmptyState, ErrorState, Feld, LoadingState } from '../muster';

const KOPFTEXT = 'Gegliedert nach den Kostengruppen der DIN 276-1:2018-12. Jeder Wert '
  + 'trägt seine Herkunft; fehlende Werte stehen als Gedankenstrich und sind nicht '
  + 'ersetzt.';

/**
 * Die Funktionale Leistungsbeschreibung, gegliedert nach DIN 276.
 *
 * --- Der Aufbau folgt der Heizung ---------------------------------------------
 *
 * Links der Baum (Kostengruppe → Anlage → Komponente), rechts der gewählte
 * Knoten. Genau wie in `HeizungKaelte.tsx`; wer beide Seiten kennt, findet sich
 * ohne Umlernen zurecht.
 *
 * --- Warum die Herkunft an jedem Wert steht -----------------------------------
 *
 * Weil der Wert sonst nicht verwendbar ist. „18.001 kW" ist im
 * Vergabeverfahren etwas anderes, je nachdem ob es im Modell gemessen, aus
 * einem Herstellerdatenblatt übernommen oder als Parameter festgelegt wurde.
 * Ein fehlender Wert steht als Gedankenstrich und wird NICHT ersetzt — auch
 * nicht durch eine Null.
 *
 * --- Was hier gepflegt wird ----------------------------------------------------
 *
 * Nur die TEXTE der sieben Abschnitte. Die Zahlen kommen aus den Rechenmodulen
 * und sind hier nicht änderbar; wer sie ändern will, ändert die Berechnung.
 */

interface Rahmen { api: ApiClient; projekt?: { id: string } }

type Knotenart = 'kapitel' | 'anlage' | 'komponente';

interface Auswahl {
  art: Knotenart;
  kostengruppe: string;
  /** Kennung der Anlage bzw. Bezeichnung der Komponentengruppe. */
  name?: string;
}

const AbschnittsNamen: Record<number, string> = {
  1: 'Aufgabe und Funktion',
  2: 'Anlagenliste',
  3: 'Komponenten',
  4: 'Anforderungen',
  5: 'Schnittstellen',
  6: 'Nachweise',
  7: 'Offene Punkte',
};

export function Funktionalbeschreibung() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [baum, setBaum] = useState<FlbBaum | null>(null);
  const [gewerk, setGewerk] = useState<string>('');
  const [daten, setDaten] = useState<FlbGewerk | null>(null);
  const [gewaehlt, setGewaehlt] = useState<Auswahl | null>(null);
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [suche, setSuche] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);

  useEffect(() => {
    if (!projekt) return;
    let abgebrochen = false;

    (async () => {
      try {
        const b = await api.flbBaum(projekt.id);
        if (abgebrochen) return;
        setBaum(b);

        // Das erste Gewerk mit belegter Kostengruppe vorwählen - eine leere
        // Seite sagt nichts darüber, ob es nichts gibt oder nichts gewählt ist.
        const erstes = b.gruppen.find((g) => g.istImProjekt && g.ebene === 3
                                          && g.gewerk)?.gewerk;
        if (erstes) setGewerk(erstes);
      } catch (e) {
        if (!abgebrochen) setFehler(String(e));
      }
    })();

    return () => { abgebrochen = true; };
  }, [api, projekt]);

  useEffect(() => {
    if (!projekt || !gewerk) return;
    let abgebrochen = false;

    setLaedt(true);
    (async () => {
      try {
        const d = await api.flbGewerk(projekt.id, gewerk);
        if (abgebrochen) return;
        setDaten(d);
        setGewaehlt(d.kapitel.length > 0
          ? { art: 'kapitel', kostengruppe: d.kapitel[0].kostengruppe }
          : null);
        setOffen(new Set(d.kapitel.map((k) => k.kostengruppe)));
        setFehler(null);
      } catch (e) {
        if (!abgebrochen) setFehler(String(e));
      } finally {
        if (!abgebrochen) setLaedt(false);
      }
    })();

    return () => { abgebrochen = true; };
  }, [api, projekt, gewerk]);

  const gewerke = useMemo(() => {
    const namen = new Map<string, { kuerzel: string; gruppen: number }>();
    for (const g of baum?.gruppen ?? []) {
      if (!g.istImProjekt || g.ebene !== 3 || !g.gewerk) continue;
      const v = namen.get(g.gewerk) ?? { kuerzel: g.gewerkKuerzel ?? '', gruppen: 0 };
      v.gruppen += 1;
      namen.set(g.gewerk, v);
    }
    return [...namen.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [baum]);

  const kapitel = useMemo(() => {
    const s = suche.trim().toLowerCase();
    if (!s) return daten?.kapitel ?? [];
    return (daten?.kapitel ?? []).filter(
      (k) => k.kostengruppe.includes(s) || k.bezeichnung.toLowerCase().includes(s)
          || k.anlagen.some((a) => a.kennung.toLowerCase().includes(s)));
  }, [daten, suche]);

  const gewaehltesKapitel = useMemo(
    () => (daten?.kapitel ?? []).find((k) => k.kostengruppe === gewaehlt?.kostengruppe),
    [daten, gewaehlt]);

  if (!projekt) {
    return (
      <div className="inhalt">
        <PageHeader titel="Funktionale Leistungsbeschreibung" beschreibung={KOPFTEXT} />
        <EmptyState
          titel="Kein Projekt gewählt."
          erklaerung="Oben links ein Projekt wählen — die Beschreibung entsteht je Projekt."
        />
      </div>
    );
  }

  return (
    <div className="inhalt">
      <PageHeader
        titel="Funktionale Leistungsbeschreibung"
        beschreibung={KOPFTEXT}
        meta={gewerk ? `Gewerk ${gewerk}` : undefined}
        aktionen={(
          <>
            <button type="button" className="m-knopf"
              onClick={() => gewerk && api.flbBericht(projekt.id, gewerk)}
              disabled={!gewerk}>
              <IconPdf size={14} /> Heft {gewerk || '…'}
            </button>
            <button type="button" className="m-knopf m-knopf-haupt"
              onClick={() => api.flbGesamt(projekt.id)}>
              <IconPdf size={14} /> Gesamtband
            </button>
          </>
        )}
      />

      {fehler && (
        <ErrorState
          meldung="Die Funktionale Leistungsbeschreibung konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => setGewerk((g) => g)}
        />
      )}

      <Laufzeile baum={baum} />

      <div style={{ display: 'flex', gap: 6, margin: '12px 0', flexWrap: 'wrap' }}>
        {gewerke.map(([name, v]) => (
          <button
            key={name}
            type="button"
            className={gewerk === name ? 'm-knopf m-knopf-haupt' : 'm-knopf'}
            onClick={() => setGewerk(name)}
          >
            {name} <span className="schwach">{v.gruppen}</span>
          </button>
        ))}
      </div>

      {laedt && (
        <LoadingState text="Leistungsbeschreibung wird geladen …" zeilen={8} spalten={3} />
      )}

      {!laedt && !fehler && gewerke.length === 0 && (
        <EmptyState
          titel="Für dieses Projekt ist noch keine Leistungsbeschreibung abgeleitet."
          erklaerung={'Sie entsteht aus den Gewerkemodulen über den Ableitungslauf — '
            + 'ohne aufgenommene Anlagen gibt es keine Kostengruppe.'}
        />
      )}

      {!laedt && daten && (
        <div className="karte" style={{ display: 'flex', gap: 0, padding: 0 }}>
          {/* Links der Baum: Kostengruppe → Anlage → Komponente. */}
          <div className="baum">
            <div style={{ padding: '10px 12px' }}>
              <Feld art="suche" beschriftung="Kostengruppe oder Anlage" wert={suche}
                platzhalter="Suchen …"
                beiAenderung={(w) => setSuche(w as string)} />
            </div>

            {kapitel.map((k) => (
              <div key={k.kostengruppe}>
                <button
                  className={`baum-ebene ${
                    gewaehlt?.art === 'kapitel'
                    && gewaehlt.kostengruppe === k.kostengruppe ? 'aktiv' : ''}`}
                  onClick={() => {
                    setGewaehlt({ art: 'kapitel', kostengruppe: k.kostengruppe });
                    setOffen((s) => {
                      const neu = new Set(s);
                      if (neu.has(k.kostengruppe)) neu.delete(k.kostengruppe);
                      else neu.add(k.kostengruppe);
                      return neu;
                    });
                  }}
                >
                  <IconEbene size={14} />
                  <span>{k.kostengruppe} {k.bezeichnung}</span>
                  <span className="schwach">{k.anlagen.length}</span>
                </button>

                {offen.has(k.kostengruppe) && (
                  <>
                    {/* Höchstens 60 Anlagen im Baum: Die Lüftung führt 108,
                        die Raumautomation 600 - ein Baum mit 600 Knoten ist
                        keine Gliederung mehr. Der Rest steht in der Tabelle
                        rechts, die alles zeigt. */}
                    {k.anlagen.slice(0, 60).map((a) => (
                      <button
                        key={a.kennung}
                        className={`baum-zeile ${
                          gewaehlt?.art === 'anlage'
                          && gewaehlt.kostengruppe === k.kostengruppe
                          && gewaehlt.name === a.kennung ? 'aktiv' : ''}`}
                        onClick={() => setGewaehlt({
                          art: 'anlage', kostengruppe: k.kostengruppe, name: a.kennung })}
                      >
                        <span>{a.kennung}</span>
                        {a.standort && <span className="schwach">{a.standort}</span>}
                      </button>
                    ))}

                    {k.anlagen.length > 60 && (
                      <div className="baum-zeile schwach" style={{ cursor: 'default' }}>
                        … {k.anlagen.length - 60} weitere in der Tabelle
                      </div>
                    )}

                    {[...new Set(k.komponenten.map((c) => c.gruppe))].map((g) => (
                      <button
                        key={`k-${g}`}
                        className={`baum-zeile ${
                          gewaehlt?.art === 'komponente'
                          && gewaehlt.kostengruppe === k.kostengruppe
                          && gewaehlt.name === g ? 'aktiv' : ''}`}
                        onClick={() => setGewaehlt({
                          art: 'komponente', kostengruppe: k.kostengruppe, name: g })}
                      >
                        <IconTabelle size={12} />
                        <span>{g}</span>
                        <span className="schwach">
                          {k.komponenten.filter((c) => c.gruppe === g).length}
                        </span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Rechts der gewählte Knoten. */}
          <div style={{ flex: 1, padding: '14px 18px', minWidth: 0 }}>
            {!gewaehltesKapitel && (
              <EmptyState
                titel="Kein Kapitel gewählt."
                erklaerung={'Links eine Kostengruppe, Anlage oder Komponentengruppe '
                  + 'wählen — rechts erscheinen ihre Werte mit Herkunft.'}
              />
            )}

            {gewaehltesKapitel && gewaehlt?.art === 'kapitel' && (
              <Kapitelansicht
                api={api}
                projektId={projekt.id}
                gewerk={gewerk}
                kapitel={gewaehltesKapitel}
                neuLaden={async () => {
                  setDaten(await api.flbGewerk(projekt.id, gewerk));
                }}
              />
            )}

            {gewaehltesKapitel && gewaehlt?.art === 'anlage' && (
              <Anlagenansicht kapitel={gewaehltesKapitel} kennung={gewaehlt.name!} />
            )}

            {gewaehltesKapitel && gewaehlt?.art === 'komponente' && (
              <Komponentenansicht kapitel={gewaehltesKapitel} gruppe={gewaehlt.name!} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Laufzeile({ baum }: { baum: FlbBaum | null }) {
  const l = baum?.letzterLauf;
  if (!l) return null;

  return (
    <div className="karte hinweis" style={{ fontSize: 13 }}>
      Letzter Lauf {new Date(l.gestartetAm).toLocaleString('de-DE')} ·
      {' '}{l.gruppen} Kostengruppen · {l.anlagen} Anlagenzeilen ·
      {' '}{l.komponenten} Komponentenzeilen · {l.kennzahlen} belegte Kennwerte ·
      {' '}{l.offenePlatzhalter} offene Platzhalter
      {l.kiSaetze > 0 && <> · {l.kiSaetze} KI-Abschnitte</>}
      {!l.geschrieben && <span className="schwach"> (Probelauf)</span>}
    </div>
  );
}

/** Die Werteinheit: Wert und Herkunft gehören zusammen. */
function Wert({ w }: { w: FlbWert }) {
  if (!w.text) return <span className="sehr-schwach" title="liegt nicht vor">—</span>;
  return <span title={`Herkunft: ${w.herkunft}`}>{w.text}</span>;
}

const ERGEBNISNAME: Record<FlbAbgleich['ergebnis'], string> = {
  eingehalten: 'eingehalten',
  abweichend: 'abweichend',
  offen: 'offen',
};

/**
 * Der Abgleich mit dem Fachlastenbuch (TS14a): Vorgabe, Planung, Ergebnis.
 * Eine fehlende Seite steht als Gedankenstrich, das Ergebnis ist dann „offen“
 * — nicht „eingehalten“. Gruppen ohne Abgleich zeigen nichts.
 */
export function FlbAbgleichTabelle({ zeilen }: { zeilen: FlbAbgleich[] }) {
  if (zeilen.length === 0) return null;
  const abweichend = zeilen.filter((z) => z.ergebnis === 'abweichend').length;

  return (
    <>
      <h3 style={{ marginTop: 18 }}>
        Abgleich mit dem Fachlastenbuch
        {' '}<span className="schwach">{abweichend} von {zeilen.length} abweichend</span>
      </h3>
      <div className="tabellen-rahmen">
        <table>
          <thead>
            <tr>
              <th>Punkt</th><th>Fundstelle</th><th>Fachlastenbuch</th>
              <th>Planung</th><th>Ergebnis</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={`${z.punkt}-${z.fundstelle}`} title={z.hinweis ?? undefined}>
                <td>{z.punkt}</td>
                <td>{z.fundstelle}</td>
                <td>{z.fachlastenbuch ?? '—'}</td>
                <td>{z.planung ?? '—'}</td>
                <td className={z.ergebnis === 'abweichend' ? 'warnung' : undefined}>
                  {z.ergebnis === 'abweichend' && <IconWarnung size={12} />}
                  {z.ergebnis === 'eingehalten' && <IconOk size={12} />}
                  {' '}{ERGEBNISNAME[z.ergebnis] ?? z.ergebnis}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Kapitelansicht({ api, projektId, gewerk, kapitel, neuLaden }: {
  api: ApiClient; projektId: string; gewerk: string; kapitel: FlbKapitel;
  neuLaden: () => Promise<void>;
}) {
  return (
    <>
      <h2 style={{ marginTop: 0 }}>{kapitel.kostengruppe} {kapitel.bezeichnung}</h2>

      {kapitel.datenquelle && (
        <p className="sehr-schwach" style={{ fontSize: 12, marginTop: -6 }}>
          Datenquelle: {kapitel.datenquelle}
        </p>
      )}

      {kapitel.istFestlegung && (
        <div className={`karte ${kapitel.istBestaetigt ? 'hinweis' : 'warnung'}`}>
          {kapitel.istBestaetigt ? <IconOk size={14} /> : <IconWarnung size={14} />}
          {' '}{kapitel.festlegung ?? 'Die Zuordnung dieser Kostengruppe ist eine Festlegung.'}
          {!kapitel.istBestaetigt && <> <b>Vom Fachplaner nicht bestätigt.</b></>}
        </div>
      )}

      <div className="kennzahlen" style={{ margin: '12px 0' }}>
        {kapitel.kennzahlen.map((k) => (
          <div key={k.schluessel} className="karte" style={{ padding: '8px 10px' }}>
            <div className="feldname" style={{ fontSize: 11 }}>{k.bezeichnung}</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              {k.text ?? '—'}{k.text && k.einheit ? ` ${k.einheit}` : ''}
            </div>
            <div className="sehr-schwach" style={{ fontSize: 11 }}>{k.herkunft}</div>
          </div>
        ))}
      </div>

      {kapitel.anlagen.length > 0 && (
        <>
          <h3>Anlagenliste <span className="schwach">{kapitel.anlagen.length}</span></h3>
          <div className="tabellen-rahmen" style={{ maxHeight: 420, overflow: 'auto' }}>
            <table>
              <thead>
                <tr>{kapitel.spalten.map((s) => (
                  <th key={s.titel} style={{ textAlign: s.zahl ? 'right' : 'left' }}>
                    {s.titel}
                  </th>
                ))}</tr>
              </thead>
              <tbody>
                {kapitel.anlagen.map((a, i) => (
                  <tr key={`${a.kennung}-${i}`}>
                    {a.werte.map((w, j) => (
                      <td key={j} style={{
                        textAlign: kapitel.spalten[j]?.zahl ? 'right' : 'left' }}>
                        <Wert w={w} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {kapitel.luecken.length > 0 && (
        <div className="karte warnung" style={{ marginTop: 14 }}>
          <b><IconWarnung size={14} /> Offene Punkte aus dem Datenbestand</b>
          <ul style={{ margin: '6px 0 0 18px' }}>
            {kapitel.luecken.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        </div>
      )}

      <FlbAbgleichTabelle zeilen={kapitel.abgleich ?? []} />

      <h3 style={{ marginTop: 18 }}>Die sieben Abschnitte</h3>
      {kapitel.abschnitte.map((a) => (
        <Abschnittspflege
          key={a.nummer}
          api={api}
          projektId={projektId}
          gewerk={gewerk}
          kostengruppe={kapitel.kostengruppe}
          abschnitt={a}
          neuLaden={neuLaden}
        />
      ))}
    </>
  );
}

function Abschnittspflege({ api, projektId, gewerk, kostengruppe, abschnitt, neuLaden }: {
  api: ApiClient; projektId: string; gewerk: string; kostengruppe: string;
  abschnitt: FlbAbschnitt; neuLaden: () => Promise<void>;
}) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [text, setText] = useState(abschnitt.text);
  const [bestaetigt, setBestaetigt] = useState(abschnitt.istBestaetigt);
  const [meldung, setMeldung] = useState<string | null>(null);

  useEffect(() => { setText(abschnitt.text); setBestaetigt(abschnitt.istBestaetigt); },
    [abschnitt]);

  const speichern = async () => {
    try {
      await api.flbAbschnittPflegen(projektId, gewerk, kostengruppe, abschnitt.nummer, {
        titel: abschnitt.titel,
        text,
        normen: abschnitt.normen ?? undefined,
        istBestaetigt: bestaetigt,
      });
      setBearbeiten(false);
      setMeldung(null);
      await neuLaden();
    } catch (e) {
      setMeldung(String(e));
    }
  };

  return (
    <div className="karte" style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <b>{kostengruppe}.{abschnitt.nummer} {abschnitt.titel}</b>
        <span className="marke schwach" title="Herkunft des Textes">
          {abschnitt.herkunft}
        </span>
        {abschnitt.istBestaetigt && <span className="marke gut">bestätigt</span>}
        <span className="sehr-schwach" style={{ fontSize: 11 }}>
          {AbschnittsNamen[abschnitt.nummer]}
        </span>
        <span style={{ flex: 1 }} />
        <button onClick={() => setBearbeiten((b) => !b)} style={{ fontSize: 12 }}>
          {bearbeiten ? 'abbrechen' : 'bearbeiten'}
        </button>
      </div>

      {abschnitt.herkunft === 'ki' && (
        <div className="hinweis" style={{ fontSize: 12, margin: '6px 0' }}>
          Von einem Sprachmodell ausformuliert
          ({abschnitt.quelle ?? 'Anbieter nicht vermerkt'}). Die Zahlen stammen
          unverändert aus der Berechnung; der Text ist nicht freigegeben.
        </div>
      )}

      {!bearbeiten && (
        <p style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0' }}>{abschnitt.text}</p>
      )}

      {bearbeiten && (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            style={{ width: '100%', fontSize: 13, marginTop: 8 }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={bestaetigt}
                onChange={(e) => setBestaetigt(e.target.checked)}
              />
              bestätigt
            </label>
            <button className="betont" onClick={speichern}>speichern</button>
            <span className="sehr-schwach" style={{ fontSize: 11 }}>
              Wird als Text des Fachplaners abgelegt — die Vorlage bleibt daneben stehen.
            </span>
          </div>
        </>
      )}

      {meldung && <div className="fehler" style={{ fontSize: 12 }}>{meldung}</div>}

      {abschnitt.offenePlatzhalter.length > 0 && (
        <div className="warnung" style={{ fontSize: 12, marginTop: 6 }}>
          Kein Wert vorhanden für: {abschnitt.offenePlatzhalter.join(', ')}
        </div>
      )}

      {abschnitt.normen && (
        <div className="sehr-schwach" style={{ fontSize: 11, marginTop: 6 }}>
          Regelwerke: {abschnitt.normen}
        </div>
      )}
    </div>
  );
}

function Anlagenansicht({ kapitel, kennung }: { kapitel: FlbKapitel; kennung: string }) {
  const anlage = kapitel.anlagen.find((a) => a.kennung === kennung);
  if (!anlage) return <div className="leer">Anlage nicht gefunden.</div>;

  return (
    <>
      <h2 style={{ marginTop: 0 }}>{anlage.kennung}</h2>
      <p className="schwach" style={{ marginTop: -6 }}>
        {kapitel.kostengruppe} {kapitel.bezeichnung}
        {anlage.standort && <> · Standort {anlage.standort}</>}
        {anlage.versorgungsbereich && <> · versorgt {anlage.versorgungsbereich}</>}
      </p>

      <dl className="feldliste">
        {kapitel.spalten.map((s, i) => (
          <div key={s.titel} style={{ display: 'contents' }}>
            <dt>{s.titel}</dt>
            <dd><Wert w={anlage.werte[i]} /></dd>
          </div>
        ))}
      </dl>
    </>
  );
}

function Komponentenansicht({ kapitel, gruppe }: { kapitel: FlbKapitel; gruppe: string }) {
  const zeilen = kapitel.komponenten.filter((c) => c.gruppe === gruppe);

  return (
    <>
      <h2 style={{ marginTop: 0 }}>{gruppe}</h2>
      <p className="schwach" style={{ marginTop: -6 }}>
        {kapitel.kostengruppe} {kapitel.bezeichnung} · {zeilen.length} Zeilen
      </p>

      <div className="tabellen-rahmen">
        <table>
          <thead>
            <tr>
              <th>Komponente</th>
              <th style={{ textAlign: 'right' }}>Menge</th>
              <th>Einheit</th>
              <th>Herkunft</th>
              <th>Kennwert</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((c, i) => (
              <tr key={i}>
                <td>{c.bezeichnung}</td>
                <td style={{ textAlign: 'right' }}>
                  {c.menge === null || c.menge === undefined
                    ? <span className="sehr-schwach">—</span>
                    : c.menge.toLocaleString('de-DE')}
                </td>
                <td>{c.einheit}</td>
                <td className="schwach">{c.herkunft}</td>
                <td className="schwach">{c.kennwert ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {zeilen.some((c) => c.herkunft === 'bestand') && (
        <p className="sehr-schwach" style={{ fontSize: 12 }}>
          Zeilen mit der Herkunft „bestand" sind aus der Bauteiltabelle gezählt — für
          diese Bauteilart gibt es keine Mengenregel.
        </p>
      )}
    </>
  );
}
